import { useState } from 'react';
import { useTabs } from 'minimal-shared/hooks';
import { useParams, useLocation, useNavigate } from 'react-router';

import Box from '@mui/material/Box';
import { Tab, Tabs, Skeleton, Typography } from '@mui/material';

import { paths } from 'src/routes/paths';

import { useCoverageById } from 'src/api/hooks/useCoverageDataQueries';
import { SEARCH_RESTORE_QUERY_PARAM, SEARCH_RESTORE_QUERY_PARAM_VALUE } from 'src/pages/dashboard/advanced-search/constants';
import {
  getSearchOriginFromLocationState,
  getSearchRestoreSnapshotFromLocationState,
} from 'src/pages/dashboard/advanced-search/search-restore-state';

import { ActionButton } from 'src/sections/employer-data/shared/components/action-button';
import { PolicyStatusChips } from 'src/sections/employer-data/shared/components/status-chips';

import { showError } from '../shared/utils/policyUtils';
import { CoverageGeneral } from './general/coverage-general';
import { MoveCoverageModal } from './components/move-coverage-modal';
import { MoveCoverageButton } from './components/move-coverage-button';
import DynamicBreadcrumbs from '../dynamic-breadcrumbs/dynamic-breadcrumbs';
import { CoverageRatingHistory } from './general/tabs/coverage-rating-history';

// ----------------------------------------------------------------------

export function Coverage() {
  const { comboId, coverageId } = useParams<{ comboId: string; coverageId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const tabs = useTabs('general');
  const [moveCoverageOpen, setMoveCoverageOpen] = useState(false);
  const restoreSnapshot = getSearchRestoreSnapshotFromLocationState(location.state);
  const searchOrigin = getSearchOriginFromLocationState(location.state);
  const canNavigateBackToSearch = searchOrigin === 'coverage' && restoreSnapshot !== null;

  const { data, error, isError, isLoading, isPending, isSuccess } = useCoverageById(
    Number(comboId),
    Number(coverageId)
  );
  if (isLoading || isPending) {
    return <Skeleton variant="text" sx={{ fontSize: '1rem' }} data-testid="skeleton" />;
  }
  if (isError || !isSuccess) {
    return showError(coverageId, error?.message);
  }

  const coverageSummary = data.data || [];

  const ED_COV_TABS = [
    {
      value: 'general',
      label: 'General',
      component: <CoverageGeneral coverageSummary={coverageSummary} />,
    },
    { value: 'ratinghistory', label: 'Rating history', component: <CoverageRatingHistory /> },
    { value: 'communications', label: 'Communications', component: <Box /> },
    { value: 'changelog', label: 'Change log', component: <Box /> },
  ];

  const handleBackToSearchClick = () => {
    if (!restoreSnapshot) {
      return;
    }

    navigate(
      `${paths.dashboard.advanced_search}?tab=${restoreSnapshot.tabId}&${SEARCH_RESTORE_QUERY_PARAM}=${SEARCH_RESTORE_QUERY_PARAM_VALUE}`,
      {
        state: {
          searchTab: restoreSnapshot.tabId,
          searchRestoreSnapshot: restoreSnapshot,
        },
      }
    );
  };

  const renderContent = () => (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="h4">
            {coverageSummary?.mostRecentPolicy?.coverageName ?? ''}
          </Typography>
          <PolicyStatusChips
            isControllingCoverage={coverageSummary?.isControllingCoverage}
            isIneligible={coverageSummary?.isNonCompliant}
            isMultiState={coverageSummary?.isMultiState}
          />
        </Box>
        <MoveCoverageButton onClick={() => setMoveCoverageOpen(true)} />
      </Box>
      <DynamicBreadcrumbs crumbStartPath="combo" />
      {canNavigateBackToSearch ? (
        <ActionButton
          label="Back to search"
          testId="coverage-back-to-search"
          onClick={handleBackToSearchClick}
          icon="lucide:arrow-left"
          iconPosition="start"
          variant="text"
        />
      ) : null}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={tabs.value}
          onChange={tabs.onChange}
          aria-label="employer data coverage tabs"
          data-testid="employer-data-coverage-tabs"
        >
          {ED_COV_TABS.map((tab) => (
            <Tab key={tab.value} value={tab.value} label={tab.label} />
          ))}
        </Tabs>
      </Box>

      {ED_COV_TABS.map(
        (tab) => tab.value === tabs.value && <Box key={tab.value}>{tab.component}</Box>
      )}
    </Box>
  );

  return (
    <>
      {renderContent()}
      <MoveCoverageModal
        open={moveCoverageOpen}
        onClose={() => setMoveCoverageOpen(false)}
        comboId={Number(comboId ?? 0)}
        coverageId={Number(coverageId ?? 0)}
      />
    </>
  );
}
